/* eslint-disable @typescript-eslint/no-explicit-any */
import { create, StoreApi, UseBoundStore } from 'zustand';

import React from 'react';
import {
    AuthorizableRouteObject,
    RouteObjectConfig,
    RoutesMap,
} from 'scbk-services/types/route/route';
import _ from 'scbk-services/utils/lodash-extension.ts';

export const MAIN_LAYOUT_ROOT_PATH = '/landing';

interface ModuleRegistry {
    [path: string]: () => Promise<{ default: React.ComponentType<any> }>;
}

interface PrefetchedModule {
    importer: () => Promise<{ default: React.ComponentType<any> }>;
    prefetchedAt: number;
}

interface RouterRegistryState {
    routesMap: RoutesMap;
    routeObjects: AuthorizableRouteObject[];
    previousPath: string | null;
    moduleRouteObjects: Record<string, AuthorizableRouteObject[]>;

    registerToRoutesMap: (module: string, routes: RoutesMap[string]['routes']) => void;
    registerRouteObjects: (routeObjects: AuthorizableRouteObject[]) => void;
    getPreviousPath: () => string | null;

    getRouteObjects: () => AuthorizableRouteObject[];

    modules: Record<string, ModuleRegistry>;
    prefetched: Record<string, PrefetchedModule>;
    //등록
    registerModules: (key: string, modules: ModuleRegistry) => void;
    //조회
    getModuleImporter: (path: string) => (() => Promise<any>) | undefined;
    //prefetch 실행
    prefetchModule: (path: string) => Promise<void>;

    currentMenuId?: string;
    lastMenuId?: string;
    setCurrentMenuId: (menuId: string) => void;

    registerModuleRouteObjects: (module: string, routeObjects: AuthorizableRouteObject[]) => void;

    findModuleRouteObjects: (
        module: string,
        path: string,
    ) => AuthorizableRouteObject | undefined | null;

    findModuleRouteObjectsByPageId: (id: string) => AuthorizableRouteObject | undefined | null;

    findModuleRouteObjectsByPath: (path: string) => AuthorizableRouteObject | undefined | null;

    currentRouteObject: AuthorizableRouteObject | undefined;
    setCurrentRouteObject: (routeObject: AuthorizableRouteObject) => void;
    clearCurrentRouteObject: () => void;

    currentRouteObjectConfig: RouteObjectConfig | undefined;
    setCurrentRouteObjectConfig: (resolve: RouteObjectConfig | undefined) => void;
    clearCurrentRouteObjectConfig: () => void;
}

let storeInstance: UseBoundStore<StoreApi<RouterRegistryState>> | null = null;

const createStore = () =>
    create<RouterRegistryState>()((set, get) => ({
        moduleRouteObjects: {},
        routesMap: {},
        routeStack: [],
        currentIndex: 0,
        routeObjects: [],
        routeWeights: {},
        previousPath: null,
        currentRouteObject: undefined,
        currentRouteObjectConfig: undefined,
        /**
         * React Router용 라우트 객체들을 등록하는 액션
         *
         * @param routeObjects - 등록할 라우트 객체 배열
         */
        registerRouteObjects: (routeObjects: AuthorizableRouteObject[]) => {
            set((state) => {
                return {
                    routeObjects: [...state.routeObjects, ...routeObjects],
                };
            });
        },

        /**
         * 등록된 라우트 객체들을 반환하는 액션
         *
         * @returns 현재 등록된 라우트 객체 배열
         */
        getRouteObjects: () => get().routeObjects,

        /**
         * 모듈별 라우트를 라우트 맵에 등록하는 액션
         *
         * @param module - 모듈명
         * @param routes - 등록할 라우트 배열
         */
        registerToRoutesMap: (module, routes) => {
            set((state) => {
                if (!routes || routes.length === 0) {
                    return state;
                }

                if (state.routesMap[module]?.routes) {
                    const existingRoutes = state.routesMap[module].routes;
                    if (JSON.stringify(existingRoutes) === JSON.stringify(routes)) {
                        console.log(`Module ${module} routes unchanged, skipping...`);
                        return state;
                    }
                }

                return {
                    routesMap: {
                        ...state.routesMap,
                        [module]: { routes },
                    },
                };
            });
        },

        /**
         * 이전 라우트 정보를 반환하는 액션
         *
         * @returns 이전 라우트 정보
         */
        getPreviousPath: () => get().previousPath,

        /** LazyComponent 등록 */
        modules: {},
        prefetched: {},
        registerModules: (key, moduleMap) =>
            set((state) => ({ modules: { ...state.modules, [key]: moduleMap } })),
        getModuleImporter: (path) => {
            const module = path.split('/')[1];
            console.log('get().modules', get().modules);

            if (!Object.hasOwn(get().modules, module)) {
                return undefined;
            }

            const allModules = Object.entries(get().modules[module]);

            console.log('allModules, ', allModules);

            const found = allModules.find(([k]) => {
                const a = k
                    .replace('../pages', '')
                    .replace(/^\.\.\//, '')
                    .toLowerCase();
                console.log(k, a, path);
                return k
                    .replace('../pages', '')
                    .replace(/^\.\.\//, '')
                    .replace(/\.tsx$/, '')
                    .toLowerCase()
                    .includes(path.replace(`/${module}`, ''));
            });

            console.log('found, ', found);
            console.log('found?.[1], ', found?.[1]);

            return found?.[1];
        },

        prefetchModule: async (path) => {
            const importer = get().getModuleImporter(path);

            if (!importer) {
                return console.warn(`[RouterStore] No importer for ${path}`);
            }

            //이미 prefetch 됨
            if (get().prefetched[path]) return;

            try {
                const module = await importer();

                const routeObject =
                    module.routeObject ??
                    (module.default
                        ? { path, element: React.createElement(module.default) }
                        : null);

                if (!routeObject) {
                    console.warn(`[RouterStore] No routeObject or default export in ${path}`);
                    return;
                }

                const currentRoutes = get().routeObjects || [];

                get().registerRouteObjects([...currentRoutes, routeObject]);

                set((state) => ({
                    prefetched: {
                        ...state.prefetched,
                        [`${path}`]: { importer, prefetchedAt: Date.now() },
                    },
                }));

                console.error(`[RouterStore] Prefetched ${path}`);
            } catch (e) {
                console.error(`[RouterStore] Prefetch failed for ${path}`, e);
            }
        },

        setCurrentMenuId: (menuId) =>
            set((state) => ({
                lastMenuId: state.currentMenuId,
                currentMenuId: menuId,
            })),

        /**
         * 모듈 별 라우트 정보를 등록한다.
         * @param module
         * @param routes
         */
        registerModuleRouteObjects: (module: string, routes: AuthorizableRouteObject[]) => {
            if (!Array.isArray(routes)) {
                return;
            }

            set((state) => {
                const record = {
                    [module]: routes,
                };
                return {
                    moduleRouteObjects: { ...state.moduleRouteObjects, ...record },
                };
            });
        },

        /**
         *
         * @returns
         */
        findModuleRouteObjects: (module: string, path: string) => {
            const moduleRoutes = get().moduleRouteObjects;
            const routes = moduleRoutes[module];
            if (!routes || routes == null || routes.length == 0) {
                return null;
            }

            if (path.indexOf('?') > -1) {
                path = path.split('?')[0];
            }

            const result = routes.find((v) => v.fullPath == path);
            return result ? { ...result } : result;
        },

        /**
         * 화면ID로 등록된 route 정보를 찾는다.
         * @param id  화면ID
         * @returns
         */
        findModuleRouteObjectsByPageId: (id: string) => {
            const moduleRoutes = get().moduleRouteObjects;
            const routes = Object.values(moduleRoutes).flatMap((v) => v);
            if (!routes || routes == null || routes.length == 0) {
                return null;
            }

            const result = routes.find((v) => v.id === id);
            return result;
        },

        findModuleRouteObjectsByPath: (path: string) => {
            const moduleRoutes = get().moduleRouteObjects;
            const routes = Object.values(moduleRoutes).flatMap((v) => v);

            const queryIndex = path.indexOf('?');
            const pathStr = queryIndex === -1 ? path : path.slice(0, queryIndex);

            if (!routes || routes == null || routes.length == 0) {
                return null;
            }

            const result = routes.find((v) => v.fullPath === pathStr);
            return result;
        },

        /**
         *
         * @param routeObject
         */
        setCurrentRouteObject: (routeObject: AuthorizableRouteObject) => {
            set((state) => {
                return {
                    ...state,
                    currentRouteObject: routeObject,
                };
            });
        },

        /**
         *
         */
        clearCurrentRouteObject: () => {
            set((state) => {
                return {
                    ...state,
                    currentRouteObject: undefined,
                };
            });
        },

        setCurrentRouteObjectConfig: (resolve: RouteObjectConfig | undefined) => {
            set((state) => {
                return {
                    ...state,
                    currentRouteObjectConfig: resolve,
                };
            });
        },

        clearCurrentRouteObjectConfig: () => {
            set((state) => {
                return {
                    ...state,
                    currentRouteObjectConfig: undefined,
                };
            });
        },
    }));

const useRouterRegistry = (() => {
    if (_.isEmpty(storeInstance)) {
        storeInstance = createStore();
    }

    return storeInstance!;
})();

export { useRouterRegistry };
